import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    FileText, ShieldCheck, ChevronDown, Loader2, RotateCcw,
    Send, CheckCircle, IndianRupee, Building2,
    PenLine, StickyNote,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';

import {
    fetchLCBGCodes, fetchLCBGVendorClient, submitLCBGAmend,
    clearCodes, clearDetails, clearSaveResult, resetLCBGAmend,
    selectLCBGCodes, selectLCBGCodesLoading,
    selectLCBGDetails, selectLCBGDetailsLoading, selectLCBGDetailsError,
    selectLCBGAmendSaveStatus, selectLCBGAmendSaveLoading, selectLCBGAmendSaveError,
} from '../../slices/purchaseSlice/lcbgAmendSlice';

// ── Constants ──────────────────────────────────────────────────────────────────

const LCBG_TYPES   = ['LC', 'BG'];
const LCBG_TYPE_LABELS = { LC: 'Letter of Credit', BG: 'Bank Guarantee' };
const LCBG_STATUSES = ['Amend', 'Close'];

const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// ── Helpers ────────────────────────────────────────────────────────────────────

// Convert Date object → "DD-Mon-YYYY" string for the API payload
const formatDateForAPI = (val) => {
    if (!val) return '';
    const d = val instanceof Date ? val : new Date(val);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2, '0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};

const fmt = (n) => {
    const num = parseFloat(n);
    if (!n && n !== 0) return '—';
    if (isNaN(num)) return '—';
    return `₹ ${num.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
};

// ── Shared UI (compact) ───────────────────────────────────────────────────────

const inputCls =
    'w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 hover:border-gray-400 transition-all disabled:opacity-60 disabled:cursor-not-allowed';

const Label = ({ children, required }) => (
    <label className="block text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SelectIcon = ({ loading }) => (
    <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center">
        {loading
            ? <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" />
            : <ChevronDown className="h-4 w-4 text-gray-400" />}
    </div>
);

const CardHeader = ({ icon: Icon, title, action }) => (
    <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50/60 dark:bg-gray-900/40 rounded-t-xl">
        <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-indigo-500" />
            <h2 className="text-sm font-bold text-gray-800 dark:text-gray-100">{title}</h2>
        </div>
        {action}
    </div>
);

// Sub-section heading inside the single form card
const SubHeading = ({ icon: Icon, children }) => (
    <p className="flex items-center gap-1.5 text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">
        <Icon className="h-3.5 w-3.5 text-indigo-500" /> {children}
    </p>
);

// Summary panel row
const SumRow = ({ label, value }) => (
    <div className="flex justify-between gap-3">
        <dt className="text-gray-500 dark:text-gray-400 flex-shrink-0">{label}</dt>
        <dd className="font-semibold text-gray-800 dark:text-gray-100 truncate text-right" title={value || ''}>{value || '—'}</dd>
    </div>
);

const readonlyCls =
    'w-full px-3 py-2 rounded-lg border text-sm bg-gray-50 dark:bg-gray-900 text-indigo-700 dark:text-indigo-300 font-semibold border-gray-300 dark:border-gray-600 cursor-not-allowed';

const ChipGroup = ({ options, value, onChange, labelMap, colorActive = 'indigo' }) => {
    const colors = {
        indigo: 'bg-indigo-600 border-indigo-600 text-white shadow-sm',
        violet: 'bg-violet-600 border-violet-600 text-white shadow-sm',
    };
    return (
        <div className="flex gap-2">
            {options.map((opt) => (
                <button key={opt} type="button" onClick={() => onChange(opt)}
                    title={labelMap ? labelMap[opt] : opt}
                    className={`flex-1 py-2 rounded-lg text-sm font-semibold border transition-all ${
                        value === opt
                            ? colors[colorActive]
                            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-indigo-300 dark:hover:border-indigo-700'
                    }`}>
                    {opt}
                </button>
            ))}
        </div>
    );
};

// ── Main Component ─────────────────────────────────────────────────────────────

const LCBGAmend = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);

    const codes          = useSelector(selectLCBGCodes);
    const codesLoading   = useSelector(selectLCBGCodesLoading);
    const details        = useSelector(selectLCBGDetails);
    const detailsLoading = useSelector(selectLCBGDetailsLoading);
    const detailsError   = useSelector(selectLCBGDetailsError);
    const saveStatus     = useSelector(selectLCBGAmendSaveStatus);
    const saveLoading    = useSelector(selectLCBGAmendSaveLoading);
    const saveError      = useSelector(selectLCBGAmendSaveError);

    const [lcbgType,   setLcbgType]   = useState('');   // 'LC' | 'BG'
    const [lcbgStatus, setLcbgStatus] = useState('');   // 'Amend' | 'Close'
    const [selectedCode, setSelectedCode] = useState(null); // { LCBGID, LCBGVAL }

    const [amendDate,   setAmendDate]   = useState(null);
    const [closeDate,   setCloseDate]   = useState(null);
    const [tolerance,   setTolerance]   = useState('');
    const [basicValue,  setBasicValue]  = useState('');
    const [gstValue,    setGstValue]    = useState('');
    const [charges,     setCharges]     = useState('');
    const [totalValue,  setTotalValue]  = useState('');
    const [lcbgRemarks, setLcbgRemarks] = useState('');
    const [bankRemarks, setBankRemarks] = useState('');

    const [submitted, setSubmitted] = useState(false);

    // ── Effects ───────────────────────────────────────────────────────────────

    useEffect(() => {
        return () => { dispatch(resetLCBGAmend()); };
    }, [dispatch]);

    // Fetch codes when both type and status chosen
    useEffect(() => {
        if (lcbgType && lcbgStatus) {
            dispatch(clearDetails());
            setSelectedCode(null);
            dispatch(fetchLCBGCodes({ LCBGType: lcbgType, LCBGStatus: lcbgStatus }));
        } else {
            dispatch(clearCodes());
            dispatch(clearDetails());
            setSelectedCode(null);
        }
    }, [lcbgType, lcbgStatus, dispatch]);

    // Fetch vendor/client details on code selection
    useEffect(() => {
        if (selectedCode) {
            dispatch(fetchLCBGVendorClient({
                LCBGNo:   selectedCode.LCBGVAL,
                LCBGNoId: selectedCode.LCBGID,
            }));
        } else {
            dispatch(clearDetails());
        }
    }, [selectedCode, dispatch]);

    // Auto-compute total for amendment
    useEffect(() => {
        if (lcbgStatus === 'Amend') {
            const b = parseFloat(basicValue) || 0;
            const g = parseFloat(gstValue)   || 0;
            const c = parseFloat(charges)    || 0;
            setTotalValue((b + g + c).toFixed(2));
        }
    }, [basicValue, gstValue, charges, lcbgStatus]);

    // Toast on save result
    useEffect(() => {
        if (saveStatus === 'success') {
            toast.success('LC/BG ' + (lcbgStatus === 'Amend' ? 'Amendment' : 'Closure') + ' saved successfully!');
            setSubmitted(true);
        } else if (saveStatus === 'failed') {
            toast.error(saveError || 'Save failed. Please try again.');
            dispatch(clearSaveResult());
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveStatus]);

    // ── Handlers ──────────────────────────────────────────────────────────────

    const handleTypeChange = (val) => {
        setLcbgType(val);
        setLcbgStatus('');
        setSelectedCode(null);
    };

    const handleStatusChange = (val) => {
        setLcbgStatus(val);
        setSelectedCode(null);
        setAmendDate(null); setCloseDate(null); setTolerance('');
        setBasicValue(''); setGstValue(''); setCharges(''); setTotalValue('');
        setLcbgRemarks(''); setBankRemarks('');
    };

    const handleCodeChange = (e) => {
        const id    = e.target.value;
        const found = codes.find(c => String(c.LCBGID) === String(id));
        setSelectedCode(found || null);
        setAmendDate(null); setCloseDate(null); setTolerance('');
        setBasicValue(''); setGstValue(''); setCharges(''); setTotalValue('');
        setLcbgRemarks(''); setBankRemarks('');
    };

    const handleReset = useCallback(() => {
        setLcbgType('');
        setLcbgStatus('');
        setSelectedCode(null);
        setAmendDate(null); setCloseDate(null); setTolerance('');
        setBasicValue(''); setGstValue(''); setCharges(''); setTotalValue('');
        setLcbgRemarks(''); setBankRemarks('');
        setSubmitted(false);
        dispatch(resetLCBGAmend());
    }, [dispatch]);

    const handleSubmit = () => {
        const roleId    = userData?.roleId    || userData?.RID    || 0;
        const createdBy = userData?.userName || userData?.employeeId || '';

        const payload = {
            LCBGType:       lcbgType,
            LCBGStatusType: lcbgStatus,
            LCBGId:         parseInt(selectedCode?.LCBGID, 10) || 0,
            LCBGNo:         selectedCode?.LCBGVAL || '',
            AmendDate:      lcbgStatus === 'Amend' ? formatDateForAPI(amendDate) : '',
            CloseDate:      lcbgStatus === 'Close' ? formatDateForAPI(closeDate) : '',
            Tolerance:      lcbgStatus === 'Amend' ? (tolerance  || '0') : '0',
            BasicValue:     lcbgStatus === 'Amend' ? (basicValue || '0') : '0',
            GSTValue:       lcbgStatus === 'Amend' ? (gstValue   || '0') : '0',
            Charges:        lcbgStatus === 'Amend' ? (charges    || '0') : '0',
            TotalValue:     lcbgStatus === 'Amend' ? (totalValue || '0') : '0',
            LCBGRemarks:    lcbgRemarks,
            BankRemarks:    lcbgStatus === 'Amend' ? bankRemarks : '',
            CreatedBy:      String(createdBy),
            RoleId:         roleId,
        };

        dispatch(submitLCBGAmend(payload));
    };

    // ── Validation gates ──────────────────────────────────────────────────────

    const step1Valid = lcbgType && lcbgStatus && selectedCode && details && !detailsLoading;

    const step2Valid = (() => {
        if (!details) return false;
        if (lcbgStatus === 'Amend') return amendDate instanceof Date && parseFloat(basicValue) > 0;
        return closeDate instanceof Date;
    })();

    // ── Success screen ────────────────────────────────────────────────────────

    if (submitted) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-4 md:p-6 flex items-center justify-center">
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-10 max-w-sm w-full text-center">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mx-auto mb-5 shadow-lg">
                        <CheckCircle className="h-8 w-8 text-white" />
                    </div>
                    <h2 className="text-xl font-black text-gray-900 dark:text-white mb-2">
                        {lcbgStatus === 'Amend' ? 'Amendment Saved' : 'Closure Recorded'}
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                        LC/BG {lcbgStatus === 'Amend' ? 'amendment' : 'closure'} has been submitted successfully.
                    </p>
                    <button onClick={handleReset}
                        className="w-full py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold shadow-md hover:from-indigo-700 hover:to-purple-700 transition-all">
                        New Entry
                    </button>
                </div>
            </div>
        );
    }

    // ── Render ────────────────────────────────────────────────────────────────

    const isAmend = lcbgStatus === 'Amend';

    // Single-page form — gate submit on the former step checks
    const handleSubmitClick = () => {
        if (!lcbgType)     { toast.warn('Select LC / BG type.');          return; }
        if (!lcbgStatus)   { toast.warn('Select Amend or Close.');        return; }
        if (!selectedCode) { toast.warn('Select the LC / BG number.');   return; }
        if (!step1Valid)   { toast.warn('LC / BG details are still loading.'); return; }
        if (!step2Valid) {
            toast.warn(isAmend ? 'Enter the amendment date and a valid basic value.' : 'Select the closing date.');
            return;
        }
        handleSubmit();
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-3 md:p-4">

            {/* ── Page Header (slim) ───────────────────────────────────────── */}
            <div className="max-w-7xl mx-auto mb-4">
                <div className="rounded-xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-md shadow-indigo-500/20 px-5 py-3 text-white flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center border border-white/20">
                        <PenLine className="h-5 w-5 text-white" />
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-lg font-black tracking-tight leading-tight truncate">
                            {menuData?.name || 'LC / BG Amendment & Closure'}
                        </h1>
                        <p className="text-indigo-200 text-xs">Purchase Module · Amend or close an existing Letter of Credit / Bank Guarantee</p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">

                {/* ── Left: single form card ───────────────────────────────── */}
                <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <CardHeader icon={FileText} title="LC / BG Amendment & Closure" />
                    <div className="p-4 space-y-4">

                        {/* Selection — type, action, number */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
                            <div>
                                <Label required>LC / BG Type</Label>
                                <ChipGroup options={LCBG_TYPES} value={lcbgType} onChange={handleTypeChange} labelMap={LCBG_TYPE_LABELS} colorActive="indigo" />
                            </div>
                            <div>
                                <Label required>Action</Label>
                                <ChipGroup options={LCBG_STATUSES} value={lcbgStatus} onChange={handleStatusChange} colorActive="violet" />
                            </div>
                            <div>
                                <Label required>LC / BG Number</Label>
                                <div className="relative">
                                    <select
                                        className={`${inputCls} appearance-none pr-8`}
                                        value={selectedCode?.LCBGID || ''}
                                        onChange={handleCodeChange}
                                        disabled={!lcbgType || !lcbgStatus || codesLoading}>
                                        <option value="">
                                            {!lcbgType || !lcbgStatus ? 'Select type & action first'
                                                : codesLoading ? 'Loading…'
                                                : codes.length === 0 ? 'No records found'
                                                : '— Select LC/BG No —'}
                                        </option>
                                        {codes.map(c => (
                                            <option key={c.LCBGID} value={c.LCBGID}>{c.LCBGVAL}</option>
                                        ))}
                                    </select>
                                    <SelectIcon loading={codesLoading} />
                                </div>
                                {detailsLoading && (
                                    <p className="flex items-center gap-1.5 mt-1 text-[11px] text-indigo-600 dark:text-indigo-400">
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Fetching details…
                                    </p>
                                )}
                                {detailsError && <p className="mt-1 text-[11px] text-rose-500">{detailsError}</p>}
                            </div>
                        </div>

                        {/* Reference — previous record values */}
                        {details && !detailsLoading && (
                            <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                                <SubHeading icon={Building2}>LC / BG Reference</SubHeading>
                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                                    {[
                                        { lbl: details.VendorCode ? 'Vendor' : 'Client', val: details.VendorCode || details.ClientCode || '—', raw: true },
                                        { lbl: 'PO Number',    val: details.PONO || '—', raw: true },
                                        { lbl: 'Prev Basic',   val: details.PrevSumBasicValue },
                                        { lbl: 'Prev GST',     val: details.PrevSumGSTValue },
                                        { lbl: 'Prev Charges', val: details.PrevSumCharges },
                                        { lbl: 'Prev Balance', val: details.PrevBalance, hi: true },
                                    ].map(({ lbl, val, raw, hi }) => (
                                        <div key={lbl} className={`px-2.5 py-1.5 rounded-lg border min-w-0 ${hi ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-100 dark:border-indigo-800' : 'bg-gray-50 dark:bg-gray-900/40 border-gray-100 dark:border-gray-700'}`}>
                                            <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">{lbl}</p>
                                            <p className={`text-xs font-bold truncate ${hi ? 'text-indigo-700 dark:text-indigo-300' : 'text-gray-700 dark:text-gray-200'}`} title={raw ? val : fmt(val)}>
                                                {raw ? val : fmt(val)}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Amendment / closure details */}
                        {details && !detailsLoading && (
                            <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                                <SubHeading icon={isAmend ? IndianRupee : StickyNote}>{isAmend ? 'Amendment Details' : 'Closure Details'}</SubHeading>
                                {isAmend ? (
                                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                                        <div>
                                            <Label required>Amendment Date</Label>
                                            <CustomDatePicker value={amendDate} onChange={setAmendDate} format="DD-MMM-YYYY" placeholder="Select date" />
                                        </div>
                                        <div>
                                            <Label>Tolerance (%)</Label>
                                            <input type="number" min="0" step="0.01" className={inputCls} placeholder="0.00"
                                                value={tolerance} onChange={e => setTolerance(e.target.value)} />
                                        </div>
                                        <div>
                                            <Label required>Basic Value (₹)</Label>
                                            <input type="number" min="0" step="0.01" className={inputCls} placeholder="0.00"
                                                value={basicValue} onChange={e => setBasicValue(e.target.value)} />
                                        </div>
                                        <div>
                                            <Label>GST Value (₹)</Label>
                                            <input type="number" min="0" step="0.01" className={inputCls} placeholder="0.00"
                                                value={gstValue} onChange={e => setGstValue(e.target.value)} />
                                        </div>
                                        <div>
                                            <Label>Charges (₹)</Label>
                                            <input type="number" min="0" step="0.01" className={inputCls} placeholder="0.00"
                                                value={charges} onChange={e => setCharges(e.target.value)} />
                                        </div>
                                        <div>
                                            <Label>Total Value (₹)</Label>
                                            <input type="number" readOnly className={readonlyCls} value={totalValue}
                                                placeholder="Auto" title="Basic + GST + Charges" />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                        <div>
                                            <Label required>Closing Date</Label>
                                            <CustomDatePicker value={closeDate} onChange={setCloseDate} format="DD-MMM-YYYY" placeholder="Select date" />
                                        </div>
                                    </div>
                                )}

                                {/* Remarks */}
                                <div className={`grid grid-cols-1 ${isAmend ? 'sm:grid-cols-2' : ''} gap-3 mt-3`}>
                                    <div>
                                        <Label>LC / BG Remarks</Label>
                                        <textarea rows={2} className={`${inputCls} resize-y`} placeholder="Enter remarks…"
                                            value={lcbgRemarks} onChange={e => setLcbgRemarks(e.target.value)} />
                                    </div>
                                    {isAmend && (
                                        <div>
                                            <Label>Bank Remarks</Label>
                                            <textarea rows={2} className={`${inputCls} resize-y`} placeholder="Enter bank remarks…"
                                                value={bankRemarks} onChange={e => setBankRemarks(e.target.value)} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Right: sticky summary + actions ──────────────────────── */}
                <div className="lg:sticky lg:top-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <CardHeader icon={CheckCircle} title="Summary" />
                        <div className="p-4 space-y-3">
                            <dl className="space-y-1.5 text-xs">
                                <SumRow label="Type"     value={LCBG_TYPE_LABELS[lcbgType] || lcbgType} />
                                <SumRow label="Action"   value={lcbgStatus} />
                                <SumRow label="LC/BG No" value={selectedCode?.LCBGVAL} />
                                {details?.VendorCode && <SumRow label="Vendor" value={details.VendorCode} />}
                                {details?.ClientCode && <SumRow label="Client" value={details.ClientCode} />}
                                {details?.PONO       && <SumRow label="PO No"  value={details.PONO} />}
                                {details && <SumRow label="Prev Balance" value={fmt(details.PrevBalance)} />}
                            </dl>

                            {lcbgStatus && (
                                <dl className="space-y-1.5 text-xs pt-3 border-t border-gray-100 dark:border-gray-700">
                                    {isAmend ? (
                                        <>
                                            <SumRow label="Amendment Date" value={formatDateForAPI(amendDate)} />
                                            <SumRow label="Tolerance"      value={tolerance ? `${tolerance}%` : ''} />
                                            <SumRow label="Basic"          value={fmt(basicValue)} />
                                            <SumRow label="GST"            value={fmt(gstValue)} />
                                            <SumRow label="Charges"        value={fmt(charges)} />
                                        </>
                                    ) : (
                                        <SumRow label="Closing Date" value={formatDateForAPI(closeDate)} />
                                    )}
                                </dl>
                            )}

                            {isAmend && (
                                <div className="px-4 py-3 rounded-lg bg-gradient-to-r from-indigo-500 to-violet-600 text-white">
                                    <p className="text-indigo-100 text-[10px] font-semibold uppercase tracking-wider">Amended Total Value</p>
                                    <p className="text-2xl font-black">{fmt(totalValue || 0)}</p>
                                </div>
                            )}

                            <div className="flex gap-2">
                                <button type="button" onClick={handleReset} disabled={saveLoading}
                                    className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50">
                                    <RotateCcw className="h-4 w-4" /> Reset
                                </button>
                                <button type="button" onClick={handleSubmitClick} disabled={saveLoading}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-sm font-semibold shadow-md hover:from-indigo-700 hover:to-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
                                    {saveLoading ? (
                                        <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</>
                                    ) : (
                                        <><Send className="h-4 w-4" /> Submit {lcbgStatus === 'Close' ? 'Closure' : lcbgStatus === 'Amend' ? 'Amendment' : ''}</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LCBGAmend;
